// @flow
import React, { Component, Fragment } from 'react';
import PartnerHead from 'components/partner/PartnerHead';
import PartnerContent from 'components/partner/PartnerContent';
import PartnerTags from 'components/partner/PartnerTags';
import { BaseActions, CommonActions, DisplayActions, PartnersActions } from 'store/actionCreators';
import { connect } from 'react-redux';
import type { State } from 'store';
import type { PartnerData, TocItem } from 'store/modules/partners';
import PartnerToc from 'components/partner/PartnerToc';
import QuestionModal from 'components/common/QuestionModal/QuestionModal';
import { type ContextRouter, type Location, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { convertToPlainText } from 'lib/common';
import ScrollToTop from 'components/common/ScrollToTop';
import PartnerPlaceholder from 'components/partner/PartnerPlaceholder';
import throttle from 'lodash/throttle';
import PartnerLeftSticker from 'components/partner/PartnerLeftSticker/PartnerLeftSticker';
import PartnerSeriesInfo from 'components/partner/PartnerSeriesInfo/PartnerSeriesInfo';

type Props = {
  username: ?string,
  urlSlug: ?string,
  partner: ?PartnerData,
  toc: ?(TocItem[]),
  activeHeading: ?string,
  likeInProcess: boolean,
  currentUsername: ?string,
  askRemove: boolean,
  routerHistory: Location[],
  shouldCancel: boolean,
  logged: boolean,
} & ContextRouter;

class PartnerViewer extends Component<Props> {
  onActivateHeading = throttle((headingId: string) => {
    PartnersActions.activateHeading(headingId);
  }, 250);

  initialize = async () => {
    const { username, urlSlug, shouldCancel } = this.props;
    if (!username || !urlSlug) return;
    try {
      if (shouldCancel) return;
      await PartnersActions.readPartner({
        username,
        urlSlug,
      });
      if (!this.props.partner) return;
      const { id } = this.props.partner;
      PartnersActions.getSequences(id);
    } catch (e) {
      console.log(e);
      if (e && e.response && e.response.status === 404) {
        this.props.history.replace('/404');
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.urlSlug !== this.props.urlSlug) {
      PartnersActions.unloadPartner();
      this.initialize();
    }
  }

  componentWillUnmount() {
    PartnersActions.unloadPartner();
  }

  onSetToc = (toc: ?(TocItem[])) => {
    PartnersActions.setToc(toc);
  };

  onToggleLike = () => {
    const { partner, likeInProcess } = this.props;
    if (likeInProcess) return;
    if (!partner) return;
    if (partner.liked) {
      PartnersActions.unlike(partner.id);
    } else {
      PartnersActions.like(partner.id);
    }
  };

  onToggleAskRemove = () => {
    PartnersActions.toggleAskRemove();
  };

  onConfirmRemove = async () => {
    const { partner, history, routerHistory } = this.props;
    PartnersActions.toggleAskRemove();
    if (!partner) return;
    try {
      await CommonActions.removePartner(partner.id);
      DisplayActions.flushList();
    } catch (e) {
      console.log(e);
    }
    if (routerHistory.length === 0) {
      history.push('/');
      return;
    }
    history.goBack();
  };

  componentDidMount() {
    this.initialize();
    const { hash } = this.props.location;
    if (hash !== '') {
      PartnersActions.activateHeading(decodeURI(hash.split('#')[1]));
    }
  }

  informCopy = () => {
    BaseActions.showToast({
      message: '링크가 복사되었습니다',
      type: 'success',
    });
  };

  render() {
    const {
      partner,
      toc,
      activeHeading,
      username,
      currentUsername,
      askRemove,
      logged,
    } = this.props;
    const { onSetToc, onActivateHeading } = this;

    if (!partner) return <PartnerPlaceholder />;
    if (!partner.user) return <PartnerPlaceholder />;
    const plainTextBody = convertToPlainText(partner.body);

    const url = `${process.env.BASE_URL}/@${partner.user.username}/partners/${partner.url_slug}`;
    const baseTitle = `${partner.title} | 캐시바이`;
    return (
      <Fragment>
        <Helmet>
          <title>{baseTitle}</title>
          <meta name="description" content={plainTextBody} />
          <link rel="canonical" href={url} />
          <meta property="og:url" content={url} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={baseTitle} />
          <meta property="og:description" content={plainTextBody} />
          {partner.thumbnail && <meta property="og:image" content={partner.thumbnail} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={baseTitle} />
          <meta name="twitter:description" content={plainTextBody} />
          {partner.thumbnail && <meta name="twitter:image" content={partner.thumbnail} />}
        </Helmet>
        <PartnerLeftSticker
          logged={logged}
          likes={partner.likes}
          liked={partner.liked}
          onToggleLike={this.onToggleLike}
          url={this.props.match.url}
          title={partner.title}
          username={partner.user.username}
          informCopy={this.informCopy}
          disabled={!logged}
        />
        <PartnerToc
          toc={toc}
          activeHeading={activeHeading}
          onActivateHeading={this.onActivateHeading}
        />
        <PartnerHead
          id={partner.id}
          date={partner.released_at}
          title={partner.title}
          tags={partner.tags}
          categories={partner.categories}
          user={partner.user}
          likes={partner.likes}
          liked={partner.liked}
          onToggleLike={this.onToggleLike}
          ownPartner={currentUsername === username}
          onAskRemove={this.onToggleAskRemove}
          logged={logged}
          isPrivate={partner.is_private}
          url={this.props.match.url}
          informCopy={this.informCopy}
        />
        {partner.series && <PartnerSeriesInfo series={partner.series} username={username || ''} />}
        <PartnerContent
          thumbnail={partner.thumbnail}
          body={partner.body}
          onSetToc={onSetToc}
          onActivateHeading={onActivateHeading}
          theme={partner.meta.code_theme}
        />
        <PartnerTags tags={partner.tags} />
        <QuestionModal
          open={askRemove}
          title="파트너 삭제"
          description="이 파트너를 정말로 삭제하시겠습니까?"
          confirmText="삭제"
          onConfirm={this.onConfirmRemove}
          onCancel={this.onToggleAskRemove}
        />
      </Fragment>
    );
  }
}

export default connect(
  ({ partners, pender, user, common }: State) => ({
    currentUsername: user.user && user.user.username,
    partner: partners.partner,
    toc: partners.toc,
    activeHeading: partners.activeHeading,
    likeInProcess: pender.pending['partners/LIKE'] || pender.pending['partners/UNLIKE'],
    askRemove: partners.askRemove,
    routerHistory: common.router.history,
    shouldCancel: common.ssr && !common.router.altered,
    logged: !!user.user,
  }),
  () => ({}),
)(withRouter(PartnerViewer));



// WEBPACK FOOTER //
// ./src/containers/partner/PartnerViewer.js